import anime from 'animejs';

document.addEventListener('DOMContentLoaded', () => {
	const flippers = document.querySelectorAll<HTMLElement>(
		'[data-text-flipper]',
	);

	flippers.forEach((flipper) => {
		const words = (flipper.getAttribute('data-text-flipper') || '').split(';');

		if (words.length <= 0) return;

		// Keep track of where in the series of words we are
		let currentWord = 0;

		// Time to wait between flips
		const wait = 3000;

		flipper.style.whiteSpace = 'nowrap';
		flipper.style.display = 'inline-block';

		const queuedFlip = () => {
			const nextWord = getNext(currentWord, words.length);

			flip(flipper, words[currentWord], words[nextWord]).then(() => {
				currentWord = nextWord;
			});
		};

		let intervalId: number | undefined = window.setInterval(queuedFlip, wait);

		// Clear the interval when window goes to the background
		window.addEventListener('blur', () => {
			window.clearInterval(intervalId);
			intervalId = undefined;
		});

		// Set up interval when window gets focus
		window.addEventListener('focus', () => {
			if (intervalId === undefined) {
				intervalId = window.setInterval(queuedFlip, wait);
			}
		});
	});
});

/**
 * Perform the flip animation
 *
 * @param element The parent element
 * @param currentWord The word to flip from
 * @param nextWord The word to flip to
 * @param duration Duration on animation
 * @returns Promise that resolves when animation is finished
 */
const flip = async (
	element: HTMLElement,
	currentWord: string,
	nextWord: string,
	duration = 800,
) => {
	const easing = 'cubicBezier(.8, 0, 0, 1.03)';
	element.style.position = 'relative';

	while (element.firstChild) {
		element.removeChild(element.firstChild);
	}

	const [current, currentWidth] = createNode(element, currentWord);
	const [next, nextWidth] = createNode(element, nextWord);

	current.style.display = 'inline-block';
	next.style.position = 'absolute';
	next.style.top = '0px';
	next.style.left = '0px';

	const timeline = anime.timeline({
		easing,
		duration,
	});

	timeline.add(
		{
			targets: element,
			width: [currentWidth, nextWidth],
			complete: () => {
				element.style.width = '';
			},
		},
		0,
	);

	timeline.add(
		{
			targets: current,
			translateX: ['0rem', '-4rem'],
			opacity: [1, 0],
		},
		0,
	);

	timeline.add(
		{
			targets: next,
			translateX: ['4rem', '0rem'],
			opacity: [0, 1],
		},
		0,
	);

	await timeline.finished;

	while (element.firstChild) {
		element.removeChild(element.firstChild);
	}

	element.innerText = nextWord;
	return;
};

/**
 * Get the next number in looping sequence
 * @param current The number to increment from
 * @param total The maximum number
 * @returns The next number
 */
const getNext = (current: number, total: number) => {
	return (current + 1) % total;
};

/**
 * Create and insert a DOM node. Return a reference to the DOM node
 * and the width of it.
 * @param word The word to measure
 * @returns Pixels of width
 */
const createNode = (
	parent: HTMLElement,
	word: string,
): [HTMLElement, number] => {
	const node = document.createElement('span');
	node.textContent = word;
	parent.appendChild(node);

	const { width } = node.getBoundingClientRect();

	return [node, width];
};
